import React, { useEffect, useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import {
  Button,
  DatePicker,
  Dropdown,
  FileUploader,
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
  Pagination,
  SearchInput,
  Tag,
  TagAppearance,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import {
  getUserListHIDSagaAction,
  getUserListSagaAction,
} from "../../../../../../../redux/saga/user/saga-actions";
import { getBenefitsByTierSagaAction } from "../../../../../../../redux/saga/partner-program/benefits/saga-actions";
import { getPartnerTierListSagaAction } from "../../../../../../../redux/saga/partner-program/saga-actions";
import {
  getBenefitsLogSagaAction,
  upsertBenefitsLogSagaAction,
  deleteBenefitsLogSagaAction,
} from "../../../../../../../redux/saga/partner-program/benefits-log/saga-actions";
import { benefitsLogSliceAction } from "../../../../../../../redux/slices/partner-program/benefits-log";
import { BenefitActivityLogItemType } from "../../../../../../../redux/slices/partner-program/benefits-log/Types";
import { sortLogsLatestFirst } from "../../../../../../../redux/slices/partner-program/benefits-log/sortLogs";
import {
  CREATE_STATUS_OPTIONS,
  BenefitLogFile,
  BenefitLogHistoryRow,
  DropdownOption,
} from "./mockData";
import {
  formatDisplayDate,
  formatFileSizeLabel,
  formatUploadedBy,
  getPlainTextLength,
  mapActivityLogToHistoryRow,
  statusClassName,
} from "./benefitsLogUtils";
import { sanitizeRichText } from "../../../../../../../common/utils/richText";
import RichTextEditor from "../../../../../../../common/components/RichTextEditor";
import ViewCommentsModal from "./ViewCommentsModal";
import DeleteBenefitLogModal from "./DeleteBenefitLogModal";
import DeleteModal from "../../../../../../../common/components/DeleteModal";
import useBenefitMediaPreview from "./useBenefitMediaPreview";
import useBenefitLogFilterOptions from "./useBenefitLogFilterOptions";
import BenefitsLogFilterModal, {
  BenefitsLogFilterState,
  EMPTY_BENEFITS_LOG_FILTER,
} from "./BenefitsLogFilterModal";
import { ReactComponent as UploadFilesIcon } from "../../../../../../../common/assets/icons/upload_files.svg";
import { useUnsavedChanges } from "../../unsaved-changes/UnsavedChangesContext";
import "./BenefitsLog.scss";

type Props = {
  readOnly?: boolean;
};

type FormState = {
  benefit: DropdownOption | null;
  plannedDate: Date | null;
  deliveredDate: Date | null;
  partnerOwners: DropdownOption[];
  hidOwners: DropdownOption[];
  createStatus: DropdownOption | null;
  comment: string;
  files: BenefitLogFile[];
  deletedMediaIds: number[];
};

const normalizeCommentForCompare = (comment: string = ""): string =>
  getPlainTextLength(comment) > 0 ? comment.trim() : "";

// The rich-text editor re-serializes loaded comments (e.g. wraps plain text in <p>),
// which must not count as a user change.
const canonicalizeCommentForCompare = (comment: string = ""): string => {
  const normalized = normalizeCommentForCompare(comment);
  if (!normalized) return "";
  if (!/<[a-z][\s\S]*>/i.test(normalized)) return `<p>${normalized}</p>`;
  return normalized.replace(/>\s+</g, "><").replace(/\s+/g, " ");
};

const createEmptyForm = (): FormState => ({
  benefit: null,
  plannedDate: null,
  deliveredDate: null,
  partnerOwners: [],
  hidOwners: [],
  createStatus: null,
  comment: "",
  files: [],
  deletedMediaIds: [],
});

const serializeFormForCompare = (formState: FormState) =>
  JSON.stringify({
    benefit: formState.benefit?.value || "",
    plannedDate: formState.plannedDate?.getTime() || null,
    deliveredDate: formState.deliveredDate?.getTime() || null,
    partnerOwners: formState.partnerOwners.map((owner) => owner.value).sort(),
    hidOwners: formState.hidOwners.map((owner) => owner.value).sort(),
    createStatus: formState.createStatus?.value || "",
    comment: canonicalizeCommentForCompare(formState.comment),
    files: formState.files.map((file) => file.id),
  });

const emptyForm: FormState = createEmptyForm();

const formatApiDate = (date: Date | null): string => {
  if (!date) return "";
  return date.toLocaleDateString("en-CA");
};

const parseApiDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const mapUsersToOwnerOptions = (
  users?: Array<{
    ID: number;
    full_name?: string;
    first_name?: string;
    last_name?: string;
  }>
): DropdownOption[] =>
  (users || []).map((user) => ({
    label:
      user.full_name ||
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      String(user.ID),
    value: String(user.ID),
  }));

const mapMediaToFormFiles = (
  media?: BenefitActivityLogItemType["media"]
): BenefitLogFile[] =>
  (media || []).map((file) => ({
    id: `existing-${file.ID}`,
    name: file.file_name || "-",
    sizeLabel: formatFileSizeLabel(file.size_bytes),
    uploadDate: formatDisplayDate(file.upload_date),
    uploadedBy: formatUploadedBy(file.uploaded_by),
    filePath: file.file_path || undefined,
    mediaId: file.ID,
    isExisting: true,
  }));

const HISTORY_SEARCH_DEBOUNCE_MS = 350;
const COMMENT_MAX_LENGTH = 1000;

const mapUsersToOptions = (
  users: Array<{ ID: number; first_name: string; last_name: string }>
): DropdownOption[] =>
  (users || []).map((user) => ({
    label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.ID.toString(),
    value: String(user.ID),
  }));

const RequiredMark: React.FC = () => (
  <span className="bl-required" aria-hidden="true">
    *
  </span>
);
const BenefitsLog: React.FC<Props> = ({ readOnly = false }) => {
  const dispatch = useAppDispatch();
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { userList, userListHID } = useAppSelector((state) => state.user);
  const { partnerTierList } = useAppSelector((state) => state.partner_program);
  const {
    logs: benefitActivityLogs,
    pagination: historyPagination,
    status: { loading: benefitsLogLoading, saving: benefitsLogSaving },
  } = useAppSelector((state) => state.partner_program_benefits_log);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { setDirty, registerDiscardHandler, requestNavigation } =
    useUnsavedChanges();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [formBaseline, setFormBaseline] = useState(
    serializeFormForCompare(emptyForm)
  );
  const [toasts, setToasts] = useState<any[]>([]);
  const [isUploadZoneOpen, setIsUploadZoneOpen] = useState(true);
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [viewModalRow, setViewModalRow] = useState<BenefitLogHistoryRow | null>(null);
  const [pendingRemoveFile, setPendingRemoveFile] =
    useState<BenefitLogFile | null>(null);
  const [deleteModalRow, setDeleteModalRow] =
    useState<BenefitLogHistoryRow | null>(null);
  const [benefitOptions, setBenefitOptions] = useState<DropdownOption[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [appliedHistorySearch, setAppliedHistorySearch] = useState("");
  const [historyFilter, setHistoryFilter] = useState<BenefitsLogFilterState>(
    EMPTY_BENEFITS_LOG_FILTER
  );

  const historyRows = useMemo(
    () => sortLogsLatestFirst(benefitActivityLogs).map(mapActivityLogToHistoryRow),
    [benefitActivityLogs]
  );

  const partnerTierId = useMemo(() => {
    const rawTier = (partnerDetails?.partner_tier || "").trim();
    if (!rawTier || !partnerTierList?.length) return null;
    const tierName = rawTier.toLowerCase();
    const matched = partnerTierList.find(
      (tier) =>
        (tier.tier_name || "").trim().toLowerCase() === tierName ||
        String(tier.ID) === rawTier
    );
    return matched?.ID ?? null;
  }, [partnerDetails?.partner_tier, partnerTierList]);

  useEffect(() => {
    if (partnerDetails?.ID) {
      dispatch(getUserListSagaAction(partnerDetails.ID));
    } else {
      dispatch(benefitsLogSliceAction.clearBenefitsLogList());
    }
    if (!userListHID.length) {
      dispatch(getUserListHIDSagaAction());
    }
    if (!partnerTierList.length) {
      dispatch(getPartnerTierListSagaAction(1));
    }
  }, [dispatch, partnerDetails?.ID, userListHID.length, partnerTierList.length]);

  useEffect(() => {
    if (!partnerTierId) {
      setBenefitOptions([]);
      return;
    }
    dispatch(
      getBenefitsByTierSagaAction({
        tierId: partnerTierId,
        onSuccess: (options) => {
          setBenefitOptions(
            options.map((option) => ({
              label: option.label,
              value: option.value,
            }))
          );
        },
        onError: () => setBenefitOptions([]),
      })
    );
  }, [dispatch, partnerTierId]);

  const partnerOwnerOptions = useMemo(
    () => mapUsersToOptions(userList),
    [userList]
  );

  const hidOwnerOptions = useMemo(
    () => mapUsersToOptions(userListHID),
    [userListHID]
  );

  const hasActiveHistoryFilters = Object.values(historyFilter).some(
    (values) => values.length > 0
  );

  const historyFilterOptions = useBenefitLogFilterOptions(
    { type: "partner", partnerId: partnerDetails?.ID },
    true,
    filterModalOpen
  );

  useEffect(() => {
    const nextSearch = historySearchTerm.trim();
    if (nextSearch === appliedHistorySearch) return;
    const timer = window.setTimeout(() => {
      setAppliedHistorySearch(nextSearch);
      setCurrentPageNumber(1);
    }, HISTORY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [historySearchTerm, appliedHistorySearch]);

  useEffect(() => {
    if (!partnerDetails?.ID) return;
    dispatch(
      getBenefitsLogSagaAction({
        partnerId: partnerDetails.ID,
        query: {
          page: currentPageNumber,
          limit: pageSize,
          search: appliedHistorySearch || undefined,
          benefitIds: historyFilter.benefitsName,
          statuses: historyFilter.status,
          hidUserIds: historyFilter.hidOwner,
          partnerUserIds: historyFilter.partnerOwner,
          deliveredDateRange: historyFilter.deliveredDateRange,
          plannedDateRange: historyFilter.plannedDateRange,
        },
      })
    );
  }, [
    dispatch,
    partnerDetails?.ID,
    currentPageNumber,
    pageSize,
    appliedHistorySearch,
    historyFilter,
  ]);

  const totalHistoryRecords = historyPagination.totalRecords;

  // After deleting the last row on the final page, step back to a page that exists.
  useEffect(() => {
    if (benefitsLogLoading || historyRows.length > 0) return;
    const maxPage = Math.max(1, Math.ceil(totalHistoryRecords / pageSize));
    if (currentPageNumber > maxPage) {
      setCurrentPageNumber(maxPage);
    }
  }, [
    benefitsLogLoading,
    historyRows.length,
    currentPageNumber,
    pageSize,
    totalHistoryRecords,
  ]);

  const handleHistoryPageChange = (clickedPageNumber: number | null) => {
    if (clickedPageNumber) {
      setCurrentPageNumber(clickedPageNumber);
    }
  };

  const handleHistoryPageSizeChange = (_e: unknown, value: string) => {
    setPageSize(Number(value));
    setCurrentPageNumber(1);
  };

  const handleApplyHistoryFilter = (nextFilter: BenefitsLogFilterState) => {
    setHistoryFilter(nextFilter);
    setCurrentPageNumber(1);
  };

  const commentTextLength = getPlainTextLength(form.comment);
  const isCommentTooLong = commentTextLength > COMMENT_MAX_LENGTH;

  const isDeliveredStatus =
    (form.createStatus?.value || "").toLowerCase() === "delivered";

  const isEditMode = editingLogId !== null;

  const hasUnsavedChanges = useMemo(
    () => serializeFormForCompare(form) !== formBaseline,
    [form, formBaseline]
  );

  const hasRequiredFields =
    Boolean(form.benefit) &&
    form.hidOwners.length > 0 &&
    Boolean(form.plannedDate) &&
    (!isDeliveredStatus || Boolean(form.deliveredDate));

  const canSaveLogEntry =
    (isEditMode || hasUnsavedChanges) && hasRequiredFields && !isCommentTooLong;

  useEffect(() => {
    setDirty(readOnly ? false : hasUnsavedChanges);
  }, [hasUnsavedChanges, readOnly, setDirty]);

  useEffect(() => {
    return () => {
      setDirty(false);
    };
  }, [setDirty]);

  const showToast = (
    message: string,
    variant:
      | typeof NotificationVariants.Success
      | typeof NotificationVariants.Error = NotificationVariants.Success
  ) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant,
        icon: (
          <Iconography
            icon={
              variant === NotificationVariants.Success ? "circleCheck" : "ban"
            }
            size={IconographySizes.Medium}
          />
        ),
      },
    ]);
  };

  const handleOpenFile = useBenefitMediaPreview((message) =>
    showToast(message, NotificationVariants.Error)
  );

  const resetForm = () => {
    const clearedForm = createEmptyForm();
    setForm(clearedForm);
    setEditingLogId(null);
    setFormBaseline(serializeFormForCompare(clearedForm));
    setFormInstanceKey((key) => key + 1);
    setIsUploadZoneOpen(true);
    setDirty(false);
  };

  useEffect(() => {
    registerDiscardHandler(() => {
      const clearedForm = createEmptyForm();
      setDirty(false);
      setForm(clearedForm);
      setEditingLogId(null);
      setFormBaseline(serializeFormForCompare(clearedForm));
      setFormInstanceKey((key) => key + 1);
      setIsUploadZoneOpen(true);
    });
    return () => registerDiscardHandler(null);
  }, [registerDiscardHandler, setDirty]);

  const populateFormFromLog = (log: BenefitActivityLogItemType) => {
    const benefitOption =
      benefitOptions.find((option) => option.value === String(log.benefit_id)) ||
      (log.benefit_id
        ? {
            label: log.benefits_name || String(log.benefit_id),
            value: String(log.benefit_id),
          }
        : null);

    const statusOption =
      CREATE_STATUS_OPTIONS.find(
        (option) =>
          option.value.toLowerCase() === (log.status || "").toLowerCase()
      ) ||
      (log.status
        ? { label: log.status, value: log.status }
        : null);

    const nextForm: FormState = {
      benefit: benefitOption,
      plannedDate: parseApiDate(log.planned_date),
      deliveredDate: parseApiDate(log.delivered_date),
      partnerOwners: mapUsersToOwnerOptions(log.partner_user_id),
      hidOwners: mapUsersToOwnerOptions(log.HID_user_id),
      createStatus: statusOption,
      comment: normalizeCommentForCompare(log.comments || ""),
      files: mapMediaToFormFiles(log.media),
      deletedMediaIds: [],
    };

    setEditingLogId(log.ID);
    setForm(nextForm);
    setFormBaseline(serializeFormForCompare(nextForm));
    setFormInstanceKey((key) => key + 1);
    setIsUploadZoneOpen(!(log.media && log.media.length > 0));
    setDirty(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEditLog = (rowId: string) => {
    if (readOnly) return;
    const log = benefitActivityLogs.find((item) => String(item.ID) === rowId);
    if (!log) return;
    requestNavigation(() => populateFormFromLog(log));
  };

  const handleDeleteLogClick = (row: BenefitLogHistoryRow) => {
    if (readOnly) return;
    setDeleteModalRow(row);
  };

  const handleConfirmDeleteLog = () => {
    if (readOnly || !deleteModalRow || !partnerDetails?.ID) return;
    const deletingId = Number(deleteModalRow.id);
    dispatch(
      deleteBenefitsLogSagaAction({
        id: deletingId,
        partnerId: partnerDetails.ID,
        onSuccess: (message) => {
          showToast(
            message || "Benefit activity log deleted successfully."
          );
          if (editingLogId === deletingId) {
            resetForm();
          }
          setDeleteModalRow(null);
        },
        onError: (message) => {
          showToast(
            message || "Unable to delete benefit activity log.",
            NotificationVariants.Error
          );
        },
      })
    );
  };

  const addOwner = (
    key: "partnerOwners" | "hidOwners",
    option: DropdownOption | null
  ) => {
    if (!option) return;
    setForm((prev) => {
      if (prev[key].some((item) => item.value === option.value)) return prev;
      return { ...prev, [key]: [...prev[key], option] };
    });
  };

  const removeOwner = (key: "partnerOwners" | "hidOwners", value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item.value !== value),
    }));
  };

  const handleCommentChange = (value: string) => {
    if (readOnly) return;
    setForm((prev) => ({ ...prev, comment: value }));
  };

  const handleFileUpload = (
    _e: unknown,
    selectedFiles: Array<{ fileDetails?: File }> | null
  ) => {
    if (!selectedFiles?.length) return;
    const uploadedAt = formatDisplayDate(new Date());
    const uploadedByName = loggedUserDetail
      ? `${loggedUserDetail.first_name || ""} ${loggedUserDetail.last_name || ""}`.trim() ||
        "Current User"
      : "Current User";

    const nextFiles: BenefitLogFile[] = selectedFiles
      .map((item, index) => {
        const file = item?.fileDetails;
        if (!(file instanceof File)) return null;
        const sizeKb = Math.max(1, Math.round(file.size / 1024));
        return {
          id: `local-file-${Date.now()}-${index}`,
          name: file.name || `File_${index + 1}`,
          sizeLabel: `${sizeKb}KB of ${sizeKb}KB`,
          uploadDate: uploadedAt,
          uploadedBy: uploadedByName,
          file,
          isExisting: false,
        };
      })
      .filter(Boolean) as BenefitLogFile[];

    if (!nextFiles.length) return;
    setForm((prev) => ({ ...prev, files: [...prev.files, ...nextFiles] }));
    setIsUploadZoneOpen(false);
  };

  const removeFile = (fileId: string) => {
    setForm((prev) => {
      const files = prev.files.filter((file) => file.id !== fileId);
      return { ...prev, files };
    });
  };

  const handleRemoveFileClick = (file: BenefitLogFile) => {
    if (readOnly) return;
    if (file.mediaId) {
      setPendingRemoveFile(file);
      return;
    }
    removeFile(file.id);
  };

  const handleConfirmRemoveFile = () => {
    if (readOnly || !pendingRemoveFile) return;
    const { id, mediaId } = pendingRemoveFile;
    setForm((prev) => ({
      ...prev,
      files: prev.files.filter((file) => file.id !== id),
      deletedMediaIds:
        mediaId && !prev.deletedMediaIds.includes(mediaId)
          ? [...prev.deletedMediaIds, mediaId]
          : prev.deletedMediaIds,
    }));
    setPendingRemoveFile(null);
  };

  React.useEffect(() => {
    if (form.files.length === 0) {
      setIsUploadZoneOpen(true);
    }
  }, [form.files.length]);

  const handleSaveLogEntry = () => {
    if (
      !canSaveLogEntry ||
      readOnly ||
      benefitsLogSaving ||
      !partnerDetails?.ID ||
      !loggedUserDetail?.ID
    ) {
      return;
    }
    const stagedFiles = form.files
      .map((item) => item.file)
      .filter((file): file is File => file instanceof File);

    dispatch(
      upsertBenefitsLogSagaAction({
        ...(editingLogId ? { id: editingLogId } : {}),
        partner_id: partnerDetails.ID,
        benefit_id: form.benefit ? Number(form.benefit.value) : null,
        planned_date: form.plannedDate ? formatApiDate(form.plannedDate) : null,
        delivered_date:
          isDeliveredStatus && form.deliveredDate
            ? formatApiDate(form.deliveredDate)
            : null,
        HID_user_id: form.hidOwners.map((owner) => Number(owner.value)),
        partner_user_id: form.partnerOwners.map((owner) => Number(owner.value)),
        status: form.createStatus?.value || "Ongoing",
        comments:
          getPlainTextLength(form.comment) > 0
            ? sanitizeRichText(form.comment)
            : null,
        created_by: loggedUserDetail.ID,
        files: stagedFiles,
        ...(editingLogId && form.deletedMediaIds.length
          ? { deleted_media_ids: form.deletedMediaIds }
          : {}),
        onSuccess: (message) => {
          showToast(
            message ||
              (editingLogId
                ? "Benefit activity log updated successfully."
                : "Benefit activity log created successfully.")
          );
          resetForm();
          setCurrentPageNumber(1);
        },
        onError: (message) => {
          showToast(
            message || "Unable to save benefit activity log.",
            NotificationVariants.Error
          );
        },
      })
    );
  };

  return (
    <div className="benefits-log">
      <Notification toasts={toasts} setToasts={setToasts} />
      {!readOnly && (
      <section className="bl-card">
        <div className="bl-card-header">
          <h2>{isEditMode ? "Edit Benefit Delivery" : "Log New Benefit Delivery"}</h2>
          <p>Configure Activity Points</p>
        </div>

        <div className="bl-form-row three-cols">
          <div className="bl-field">
            <label className="bl-field-label">
              Benefit<RequiredMark />
            </label>
            <Dropdown
              id="benefits-log-benefit"
              placeholder="Select"
              theme="dark"
              options={benefitOptions}
              selected={form.benefit}
              isDisabled={readOnly}
              onSelect={(data: DropdownOption | null) =>
                setForm((prev) => ({ ...prev, benefit: data }))
              }
            />
          </div>

          <div className="bl-field">
            <label className="bl-field-label">Owner(s) from Partner</label>
            <Dropdown
              id="benefits-log-partner-owners"
              placeholder="Select"
              theme="dark"
              options={partnerOwnerOptions.filter(
                (option) =>
                  !form.partnerOwners.some((selected) => selected.value === option.value)
              )}
              selected={null}
              isDisabled={readOnly}
              onSelect={(data: DropdownOption | null) => addOwner("partnerOwners", data)}
            />
            {form.partnerOwners.length > 0 ? (
              <div className="bl-chips">
                {form.partnerOwners.map((owner) => (
                  <Tag
                    key={owner.value}
                    value={owner.label}
                    appearance={TagAppearance.Blue}
                    isCloseIcon={!readOnly}
                    onCloseClick={() => removeOwner("partnerOwners", owner.value)}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div className="bl-field">
            <label className="bl-field-label">
              Owner(s) from HID<RequiredMark />
            </label>
            <Dropdown
              id="benefits-log-hid-owners"
              placeholder="Select"
              theme="dark"
              options={hidOwnerOptions.filter(
                (option) =>
                  !form.hidOwners.some((selected) => selected.value === option.value)
              )}
              selected={null}
              isDisabled={readOnly}
              onSelect={(data: DropdownOption | null) => addOwner("hidOwners", data)}
            />
            {form.hidOwners.length > 0 ? (
              <div className="bl-chips">
                {form.hidOwners.map((owner) => (
                  <Tag
                    key={owner.value}
                    value={owner.label}
                    appearance={TagAppearance.Blue}
                    isCloseIcon={!readOnly}
                    onCloseClick={() => removeOwner("hidOwners", owner.value)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="bl-form-row three-cols">
          <div className="bl-field">
            <label className="bl-field-label">Status</label>
            <Dropdown
              id="benefits-log-create-status"
              placeholder="Select"
              theme="dark"
              options={CREATE_STATUS_OPTIONS}
              selected={form.createStatus}
              isDisabled={readOnly}
              onSelect={(data: DropdownOption | null) =>
                setForm((prev) => ({
                  ...prev,
                  createStatus: data,
                  deliveredDate:
                    (data?.value || "").toLowerCase() === "delivered"
                      ? prev.deliveredDate
                      : null,
                }))
              }
            />
          </div>

          <div className="bl-field">
            <label className="bl-field-label">
              Planned Delivery Date<RequiredMark />
            </label>
            <DatePicker
              id="benefits-log-planned-date"
              name="benefits-log-planned-date"
              label=""
              format="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              value={form.plannedDate}
              isDisabled={readOnly}
              onChange={(date) =>
                setForm((prev) => ({
                  ...prev,
                  plannedDate: Array.isArray(date) ? date[0] : date,
                }))
              }
            />
          </div>

          <div className="bl-field">
            <label className="bl-field-label">
              Delivered Date{isDeliveredStatus && <RequiredMark />}
            </label>
            <DatePicker
              id="benefits-log-delivered-date"
              name="benefits-log-delivered-date"
              label=""
              format="dd/MM/yyyy"
              placeholder="dd/mm/yyyy"
              value={form.deliveredDate}
              isDisabled={readOnly || !isDeliveredStatus}
              onChange={(date) =>
                setForm((prev) => ({
                  ...prev,
                  deliveredDate: Array.isArray(date) ? date[0] : date,
                }))
              }
            />
          </div>
        </div>

        <div className="bl-optional-section">
          <h3>Add Comments &amp; Files (Optional)</h3>

          <div className="bl-comment-header">
            <label className="bl-field-label" htmlFor="benefits-log-comment">
              Comment (Optional)
            </label>
            <span
              className={`bl-char-count${
                isCommentTooLong ? " bl-char-count--over" : ""
              }`}
            >
              {commentTextLength}/{COMMENT_MAX_LENGTH}
            </span>
          </div>
          <div id="benefits-log-comment" className="bl-comment-editor">
            <RichTextEditor
              key={formInstanceKey}
              id={`benefits-log-comment-editor-${formInstanceKey}`}
              placeholder="Add comments related to this benefit delivery"
              value={form.comment}
              disabled={readOnly}
              onChange={handleCommentChange}
            />
          </div>

          <div className="bl-files-header">
            <label className="bl-field-label">
              Files {form.files.length > 0 ? `(${form.files.length})` : "(Optional)"}
            </label>
          </div>

          {form.files.length > 0 ? (
            <>
              <div className="bl-files-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>File name</th>
                      <th>File size</th>
                      <th>Upload date</th>
                      <th>Uploaded by</th>
                      {!readOnly ? <th aria-label="Actions" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {form.files.map((file) => (
                      <tr key={file.id}>
                        <td>
                          <button
                            type="button"
                            className="bl-file-name"
                            onClick={() => handleOpenFile(file)}
                          >
                            {file.name}
                          </button>
                        </td>
                        <td>{file.sizeLabel}</td>
                        <td>{file.uploadDate}</td>
                        <td>{file.uploadedBy}</td>
                        {!readOnly ? (
                          <td>
                            <button
                              type="button"
                              className="bl-file-remove"
                              aria-label={`Remove ${file.name}`}
                              onClick={() => handleRemoveFileClick(file)}
                            >
                              <Iconography icon="xmark" size={IconographySizes.Small} />
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!readOnly && !isUploadZoneOpen ? (
                <div className="bl-files-footer">
                  <button
                    type="button"
                    className="bl-upload-files-btn"
                    onClick={() => setIsUploadZoneOpen(true)}
                  >
                    <Iconography
                      icon="arrowUpFromBracket"
                      size={IconographySizes.Small}
                    />
                    <span>UPLOAD FILES</span>
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {!readOnly && (isUploadZoneOpen || form.files.length === 0) ? (
            <>
              {form.files.length > 0 ? (
                <div className="bl-upload-close">
                  <Button
                    variant="flat"
                    label="Close"
                    normalCase={true}
                    icon={
                      <Iconography icon="xmark" size={IconographySizes.Small} />
                    }
                    onClick={() => setIsUploadZoneOpen(false)}
                  />
                </div>
              ) : null}
              <div className="bl-upload-zone">
                <span className="bl-upload-icon">
                  <UploadFilesIcon aria-hidden="true" />
                </span>
                <p className="bl-upload-title">
                  Choose a file or drag &amp; drop it here
                </p>
                <p className="bl-upload-hint">
                  JPEG, PNG, PDF, and MP4 formats, up to 50MB
                </p>
                <FileUploader
                  buttonConfig={{ label: "Browse files" }}
                  isMultiple={true}
                  onUpload={handleFileUpload}
                  validateConfig={[
                    {
                      maxFileSize: {
                        maxSizeInMb: 50,
                        message: "File exceeds 50MB size limit.",
                      },
                    },
                  ]}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="bl-form-actions">
          <Button
            variant="secondary"
            label="CANCEL"
            onClick={() => requestNavigation(() => resetForm())}
            disabled={readOnly || benefitsLogSaving}
          />
          <Button
            variant="primary"
            label={isEditMode ? "UPDATE LOG ENTRY" : "ADD LOG ENTRY"}
            onClick={handleSaveLogEntry}
            disabled={readOnly || !canSaveLogEntry || benefitsLogSaving}
          />
        </div>
      </section>
      )}

      <section className="bl-card">
        <div className="bl-card-header bl-card-header--with-action">
          <div className="bl-card-header-text">
            <h2>Log History</h2>
            <p>List of previous Log</p>
          </div>
          <div className="bl-history-toolbar">
            <div className="bl-history-search">
              <SearchInput
                theme="dark"
                id="benefits-log-history-search"
                name="benefits-log-history-search"
                placeholder="Search by benefit, comments, or status..."
                isDisplayClearIcon={true}
                value={historySearchTerm}
                onChange={(event: any) =>
                  setHistorySearchTerm(event.currentTarget.value)
                }
                onSearch={(_event, value) => {
                  setHistorySearchTerm(value);
                  setAppliedHistorySearch(value.trim());
                  setCurrentPageNumber(1);
                }}
              />
            </div>
            <button
              type="button"
              className={`bl-filter-link${
                hasActiveHistoryFilters ? " bl-filter-link--active" : ""
              }`}
              onClick={() => setFilterModalOpen(true)}
            >
              <Iconography icon="filter" size={IconographySizes.Small} />
              <span>Filter</span>
            </button>
          </div>
        </div>

        <div className="bl-history-table-wrap">
          <div className="bl-history-table-scroll">
            <table className="bl-history-table">
              <thead>
                <tr>
                  <th className="col-benefit">Benefits</th>
                  <th className="col-date">Planned Date</th>
                  <th className="col-date">Delivered Date</th>
                  <th className="col-status">Status</th>
                  <th className="col-owner">HID Owner</th>
                  <th className="col-owner">Partner Owner</th>
                  <th className="col-comments">Comments/ Files</th>
                  <th className="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {benefitsLogLoading ? (
                  <tr>
                    <td colSpan={8} className="bl-history-empty">
                      Loading log history...
                    </td>
                  </tr>
                ) : historyRows.length > 0 ? (
                  historyRows.map((row) => (
                  <tr key={row.id}>
                    <td className="col-benefit">{row.benefit}</td>
                    <td className="col-date">{row.plannedDate}</td>
                    <td className="col-date">{row.deliveredDate}</td>
                    <td className="col-status">
                      <span className={`bl-status-badge ${statusClassName(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="col-owner">{row.hidOwner}</td>
                    <td className="col-owner">{row.partnerOwner}</td>
                    <td className="col-comments">
                      {row.hasCommentsOrFiles ? (
                        <button
                          type="button"
                          className="bl-view-link"
                          onClick={() => setViewModalRow(row)}
                        >
                          View
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="col-action">
                      <div className="bl-row-actions">
                        <IconButton
                          size="small"
                          aria-label={`Edit ${row.benefit}`}
                          disabled={readOnly || benefitsLogSaving}
                          onClick={() => handleEditLog(row.id)}
                        >
                          <Iconography icon="pen" size={IconographySizes.Small} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Delete ${row.benefit}`}
                          disabled={readOnly || benefitsLogSaving}
                          onClick={() => handleDeleteLogClick(row)}
                        >
                          <Iconography icon="trashCan" size={IconographySizes.Small} />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="bl-history-empty">
                      No data available for display.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalHistoryRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={handleHistoryPageChange}
            onPageSizesClick={handleHistoryPageSizeChange}
            pageSizes={[
              { label: "20", value: "20" },
              { label: "50", value: "50" },
              { label: "100", value: "100" },
            ]}
          />
        </div>
      </section>

      <ViewCommentsModal
        open={Boolean(viewModalRow)}
        benefitName={viewModalRow?.benefit || ""}
        comment={viewModalRow?.comment || ""}
        files={viewModalRow?.files || []}
        onFileClick={handleOpenFile}
        onClose={() => setViewModalRow(null)}
      />

      <DeleteBenefitLogModal
        open={!readOnly && Boolean(deleteModalRow)}
        benefitName={deleteModalRow?.benefit || ""}
        isDeleting={benefitsLogSaving}
        onClose={() => {
          if (!benefitsLogSaving) {
            setDeleteModalRow(null);
          }
        }}
        onConfirm={handleConfirmDeleteLog}
      />

      {!readOnly && pendingRemoveFile && (
        <DeleteModal
          setDeleteOpen={(open) => {
            if (!open) setPendingRemoveFile(null);
          }}
          onDeleteButtonClick={handleConfirmRemoveFile}
          deleteButtonLoading={false}
          optionalHeader="Delete File"
          width={320}
          message={`Are you sure you want to delete "${pendingRemoveFile.name}"?\nIt will be removed from this log once you click Update Log Entry.`}
        />
      )}

      {filterModalOpen && (
        <BenefitsLogFilterModal
          filterProp={historyFilter}
          setFilterProp={handleApplyHistoryFilter}
          setModalOpen={setFilterModalOpen}
          benefitNameOptions={historyFilterOptions.benefits}
          statusOptions={historyFilterOptions.statuses}
          hidOwnerOptions={historyFilterOptions.hidOwners}
          partnerOwnerOptions={historyFilterOptions.partnerOwners}
          keepOpenEndedDateRanges
        />
      )}
    </div>
  );
};

export default BenefitsLog;
